import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Factory, Search, Edit, Trash2, Globe, RefreshCw, Database } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManufacturerModal = ({ manufacturer, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: manufacturer?.name || "",
    contact_email: manufacturer?.contact_email || "",
    contact_phone: manufacturer?.contact_phone || "",
    website: manufacturer?.website || "",
    notes: manufacturer?.notes || "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (manufacturer) {
        await axios.put(`${API}/manufacturers/${manufacturer.id}`, form);
        toast.success("Manufacturer updated");
      } else {
        await axios.post(`${API}/manufacturers`, form);
        toast.success("Manufacturer created");
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save manufacturer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-bold">{manufacturer ? "Edit Manufacturer" : "New Manufacturer"}</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Manufacturer Name *</label>
            <input
              data-testid="manufacturer-name-input"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="e.g., Ball, Canpack, Crown..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Contact Email</label>
            <input
              data-testid="manufacturer-email-input"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Contact Phone</label>
            <input
              data-testid="manufacturer-phone-input"
              type="text"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Website</label>
            <input
              data-testid="manufacturer-website-input"
              type="text"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="https://..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              data-testid="manufacturer-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 bg-secondary text-secondary-foreground rounded-lg font-medium hover:bg-secondary/80 transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="manufacturer-submit-btn"
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? "Saving..." : manufacturer ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const ManufacturersPage = () => {
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editManufacturer, setEditManufacturer] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [bcStatus, setBcStatus] = useState(null);
  const { isAdmin } = useAuth();

  const fetchManufacturers = async () => {
    try {
      const res = await axios.get(`${API}/manufacturers`);
      setManufacturers(res.data);
    } catch (err) {
      toast.error("Failed to load manufacturers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManufacturers();
    axios.get(`${API}/bc/status`).then((res) => setBcStatus(res.data)).catch(() => {});
  }, []);

  const filteredManufacturers = manufacturers.filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this manufacturer?")) return;
    try {
      await axios.delete(`${API}/manufacturers/${id}`);
      toast.success("Manufacturer deleted");
      fetchManufacturers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete manufacturer");
    }
  };

  const handleBCSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/bc/sync/vendors`);
      toast.success(`Synced ${res.data.total} vendors from Business Central (${res.data.created} new, ${res.data.updated} updated)`);
      fetchManufacturers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync from Business Central");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Manufacturers</h1>
          <p className="text-muted-foreground mt-1">Vendors synced from Business Central</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              data-testid="sync-bc-btn"
              onClick={handleBCSync}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={18} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing..." : "Sync from Business Central"}
            </button>
          )}
        </div>
      </div>

      {/* BC sync banner */}
      <div className="flex items-center gap-2 px-4 py-2 mb-4 bg-blue-950/30 border border-blue-800 rounded-lg text-sm">
        <Database size={16} className="text-blue-400" />
        <span className="text-blue-300">Vendors synced from Microsoft Business Central</span>
        {bcStatus?.connected && (
          <span className="text-muted-foreground">• Connected</span>
        )}
        {bcStatus?.last_sync && (
          <span className="text-muted-foreground">• Last sync: {new Date(bcStatus.last_sync.completed_at).toLocaleString()}</span>
        )}
      </div>

      <div className="relative max-w-md mb-6">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid="search-manufacturers"
          type="text"
          placeholder="Search manufacturers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      {loading ? (
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      ) : filteredManufacturers.length === 0 ? (
        <div className="empty-state">
          <Factory size={48} className="mb-4 opacity-50" />
          <p className="text-lg font-medium">No manufacturers found</p>
          <p className="text-sm">Click "Sync from Business Central" to import vendors</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredManufacturers.map((manufacturer) => (
            <div
              key={manufacturer.id}
              data-testid={`manufacturer-card-${manufacturer.id}`}
              className="bg-card border border-border rounded-xl p-6 card-hover"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-950/30 border border-amber-800 flex items-center justify-center">
                  <Factory size={24} className="text-amber-400" />
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button
                      data-testid={`edit-manufacturer-${manufacturer.id}`}
                      onClick={() => {
                        setEditManufacturer(manufacturer);
                        setShowModal(true);
                      }}
                      className="p-2 hover:bg-secondary rounded-lg transition-colors"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      data-testid={`delete-manufacturer-${manufacturer.id}`}
                      onClick={() => handleDelete(manufacturer.id)}
                      className="p-2 hover:bg-red-950/30 text-red-400 rounded-lg transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>

              <h3 className="text-lg font-semibold mb-1">
                {manufacturer.name}
                {manufacturer.bc_vendor_id && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 bg-blue-950/50 text-blue-400 rounded-full align-middle">
                    <Database size={10} />
                    BC
                  </span>
                )}
              </h3>
              {manufacturer.vendor_number && (
                <p className="text-xs text-muted-foreground mb-1">Vendor #{manufacturer.vendor_number}</p>
              )}
              {manufacturer.contact_email && (
                <p className="text-sm text-muted-foreground mb-1">{manufacturer.contact_email}</p>
              )}
              {manufacturer.email && !manufacturer.contact_email && (
                <p className="text-sm text-muted-foreground mb-1">{manufacturer.email}</p>
              )}
              {manufacturer.website && (
                <a
                  href={manufacturer.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-400 hover:underline flex items-center gap-1 mb-3"
                >
                  <Globe size={12} />
                  Website
                </a>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <span className="text-sm text-muted-foreground">{manufacturer.project_count || 0} projects</span>
                <span className="px-2 py-0.5 bg-amber-950/30 border border-amber-800 rounded text-amber-400 text-xs font-medium">
                  Manufacturer
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <ManufacturerModal
          manufacturer={editManufacturer}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            fetchManufacturers();
          }}
        />
      )}
    </div>
  );
};
